/**
 * Protected Route Component for Admin Pages
 *
 * This component ensures that only authenticated admin users can access protected routes.
 * If the user is not authenticated, they are redirected to the login page.
 */

import { useEffect, useState, ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { adminAuthService } from '../services/adminAuth'
import { Loader2 } from 'lucide-react'

interface AdminProtectedRouteProps {
  children: ReactNode
  requiredPermission?: string
}

export function AdminProtectedRoute({
  children,
  requiredPermission,
}: AdminProtectedRouteProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true)

        // 관리자 로그인(/admin/login)은 24시간짜리 토큰을 직접 발급하고
        // 재발급 쿠키를 심지 않는다. 되살릴 것이 없으므로 시도하지 않는다.
        const isAuth = adminAuthService.isAuthenticated()

        if (!isAuth) {
          setIsAuthenticated(false)
          setIsLoading(false)
          return
        }

        // Verify token with server
        const isValid = await adminAuthService.verifyToken()

        if (!isValid) {
          setIsAuthenticated(false)
          setIsLoading(false)
          return
        }

        setIsAuthenticated(true)

        // Check permission if required
        if (requiredPermission) {
          const hasReqPermission =
            adminAuthService.hasPermission(requiredPermission)
          setHasPermission(hasReqPermission)
        } else {
          setHasPermission(true)
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        setIsAuthenticated(false)
        setHasPermission(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [requiredPermission])

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-indigo-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Verifying admin access...
          </p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Show permission denied if user doesn't have required permission
  if (requiredPermission && !hasPermission) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.334 16.5c-.77.833-.23 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            Access Denied
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            You don't have permission to access this resource.
            <br />
            Required permission:{' '}
            <code className="text-red-600">{requiredPermission}</code>
          </p>
        </div>
      </div>
    )
  }

  // Render protected content
  return <>{children}</>
}

export default AdminProtectedRoute
